<template>
  <Teleport to="body">
    <transition name="modal-fade">
      <div v-if="modelValue" class="blm-overlay" @click.self="close">
        <div class="blm-modal">
          <div class="blm-header">
            <h2 class="blm-title"><i class="fas fa-building me-2"></i>Sucursales Correo Argentino</h2>
            <button type="button" class="btn-close btn-close-white" @click="close" />
          </div>
          <div class="blm-search p-3 border-bottom border-secondary">
            <div v-if="props.initialPostalCode" class="blm-search-context">
              <i class="fas fa-info-circle me-1"></i>
              Código postal del envío: <strong>{{ props.initialPostalCode }}</strong>.
            </div>
            <label class="form-label small text-muted">Buscar por código postal</label>
            <div class="input-group mb-2">
              <input
                v-model="postalCode"
                type="text"
                class="form-control bg-dark border-secondary text-white"
                maxlength="8"
                placeholder="Ej: 1650 o B1650BFG"
                @keyup.enter="searchByPostalCode"
              />
              <button type="button" class="btn btn-primary" :disabled="loading || !canSearch" @click="searchByPostalCode">
                <i v-if="loading" class="fas fa-spinner fa-spin"></i>
                <i v-else class="fas fa-search"></i>
              </button>
            </div>
            <div class="blm-search-options">
              <div>
                <label class="form-label small text-muted">O elegir provincia</label>
                <select v-model="selectedProvince" class="form-select bg-dark border-secondary text-white" :disabled="loading" @change="searchByProvince">
                  <option value="">Elegir provincia...</option>
                  <option v-for="province in provinces" :key="province.code" :value="province.code">{{ province.name }}</option>
                </select>
              </div>
              <div>
                <label class="form-label small text-muted">Filtrar sucursales</label>
                <input v-model="searchInput" type="text" class="form-control bg-dark border-secondary text-white"
                  placeholder="Nombre, dirección o localidad" :disabled="!agencies.length" />
              </div>
            </div>
            <p v-if="searched && !loading && !errorMsg" class="small text-muted mb-0 mt-2">
              <i class="fas fa-map-pin me-1"></i>{{ resolvedProvinceName }} · {{ filteredAgencies.length }} sucursales
              <template v-if="nearbyCount"> · {{ nearbyCount }} cercanas al CP</template>
            </p>
          </div>
          <div class="blm-body">
            <div v-if="loading" class="text-center py-5 text-muted">
              <i class="fas fa-spinner fa-spin fa-2x"></i>
            </div>
            <div v-else-if="errorMsg" class="alert alert-warning m-3">{{ errorMsg }}</div>
            <div v-else-if="!searched" class="text-center py-5 text-muted">
              Ingresá un código postal o elegí una provincia para buscar sucursales.
            </div>
            <div v-else-if="searched && !filteredAgencies.length" class="text-center py-5 text-muted">
              No se encontraron sucursales para esa búsqueda.
            </div>
            <div v-else class="blm-list">
              <button
                v-for="agency in filteredAgencies"
                :key="agencyCode(agency)"
                type="button"
                class="blm-agency"
                @click="selectAgency(agency)"
              >
                <div class="blm-agency-info">
                  <strong>{{ agency.name || agency.description }}</strong>
                  <span v-if="isNearbyAgency(agency)" class="blm-nearby">Cercana</span>
                  <small class="d-block text-muted">{{ formatAddress(agency) }}</small>
                  <small class="text-muted">Código: {{ agencyCode(agency) }}</small>
                </div>
                <i v-if="selectionMode" class="fas fa-check text-success"></i>
              </button>
            </div>
          </div>
          <div v-if="selectionMode" class="blm-footer p-3 border-top border-secondary small text-muted">
            Elegí la sucursal donde retirarás el pedido.
          </div>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<script setup>
import { ref, computed, watch, defineProps, defineEmits, defineModel } from 'vue'
import { getShippingAgencies } from '@/services/shipping'

const modelValue = defineModel({ type: Boolean, default: false })

const props = defineProps({
  initialPostalCode: { type: String, default: '' },
  selectionMode: { type: Boolean, default: false },
})

const emit = defineEmits(['branch-selected'])

const postalCode = ref('')
const searchInput = ref('')
const selectedProvince = ref('')
const provinces = ref([])
const loading = ref(false)
const errorMsg = ref('')
const agencies = ref([])
const searched = ref(false)
const resolvedProvinceName = ref('')
const lastSearchedPostalCode = ref('')
let requestId = 0

const agencyCode = (a) => a.code || a.agency_code || a.id || ''
const normalizePostalCode = (value) => String(value || '').trim().toUpperCase().replace(/[\s-]/g, '')
const isPostalCode = (value) => /^[A-Z]?\d{4}[A-Z]{0,3}$/i.test(normalizePostalCode(value))
const canSearch = computed(() => isPostalCode(postalCode.value))
const agencyPostalCode = (agency) => {
  const address = agency.location?.address || agency.address || {}
  return (
    agency.postal_code ||
    agency.postalCode ||
    agency.zip_code ||
    agency.zipCode ||
    agency.location?.postal_code ||
    agency.location?.postalCode ||
    address.postal_code ||
    address.postalCode ||
    address.zip_code ||
    address.zipCode ||
    ''
  )
}
const matchesPostalCode = (agency, postalCode) => {
  const searched = normalizePostalCode(postalCode)
  const agencyCodeValue = normalizePostalCode(agencyPostalCode(agency))
  if (!searched || !agencyCodeValue) return false
  if (searched === agencyCodeValue) return true

  const searchedDigits = searched.match(/^[A-Z]?(\d{4})/)?.[1]
  const agencyDigits = agencyCodeValue.match(/^[A-Z]?(\d{4})/)?.[1]
  return Boolean(searchedDigits && agencyDigits && searchedDigits === agencyDigits)
}
const isNearbyAgency = (agency) => {
  const postal = normalizePostalCode(lastSearchedPostalCode.value)
  const digits = postal.match(/^[A-Z]?(\d{4})/)?.[1]
  if (!digits) return false
  const nearbyCodes = String(agency.nearByPostalCode || agency.nearbyPostalCode || '')
    .split(',').map((code) => code.trim())
  return nearbyCodes.includes(digits) || matchesPostalCode(agency, postal)
}
const formatAddress = (a) => {
  const loc = a.location?.address || a.address || {}
  return [loc.streetName || loc.street, loc.streetNumber, loc.locality || loc.city]
    .filter(Boolean)
    .join(' ')
}

const filteredAgencies = computed(() => {
  const q = searchInput.value.trim().toLocaleLowerCase('es-AR')
  return agencies.value
    .filter((agency) => !q || [
      agency.name, agency.description, formatAddress(agency), agencyPostalCode(agency),
      agency.nearByPostalCode, agencyCode(agency),
    ].some((value) => String(value || '').toLocaleLowerCase('es-AR').includes(q)))
    .sort((a, b) => Number(isNearbyAgency(b)) - Number(isNearbyAgency(a)) ||
      String(a.name || '').localeCompare(String(b.name || ''), 'es-AR'))
})
const nearbyCount = computed(() => filteredAgencies.value.filter(isNearbyAgency).length)

const close = () => {
  requestId += 1
  modelValue.value = false
}

const fetchAgencies = async (params) => {
  const currentRequest = ++requestId
  loading.value = true
  errorMsg.value = ''
  agencies.value = []
  searchInput.value = ''
  searched.value = false
  try {
    const data = await getShippingAgencies(params)
    if (currentRequest !== requestId || !modelValue.value) return
    agencies.value = data.agencies || []
    if (data.provinces?.length) provinces.value = data.provinces
    selectedProvince.value = data.resolved_province || params.provinceCode || ''
    resolvedProvinceName.value = data.resolved_province_name || ''
    lastSearchedPostalCode.value = params.postalCode || ''
    searched.value = true
  } catch (e) {
    if (currentRequest !== requestId || !modelValue.value) return
    errorMsg.value = e.response?.data?.detail || e.response?.data?.error || 'No se pudieron cargar sucursales'
    if (e.response?.data?.provinces?.length) provinces.value = e.response.data.provinces
  } finally {
    if (currentRequest === requestId) loading.value = false
  }
}

const searchByPostalCode = () => {
  if (!canSearch.value) return
  selectedProvince.value = ''
  fetchAgencies({ postalCode: postalCode.value.trim() })
}

const searchByProvince = () => {
  if (!selectedProvince.value) return
  fetchAgencies({ provinceCode: selectedProvince.value })
}

const selectAgency = (agency) => {
  if (!props.selectionMode) return
  const code = agencyCode(agency)
  if (!code) return
  emit('branch-selected', { code: String(code), name: agency.name || agency.description || '' })
  close()
}

watch(
  modelValue,
  (open) => {
    if (open) {
      postalCode.value = props.initialPostalCode || ''
      searchInput.value = ''
      selectedProvince.value = ''
      agencies.value = []
      searched.value = false
      lastSearchedPostalCode.value = ''
      resolvedProvinceName.value = ''
      errorMsg.value = ''
      if (isPostalCode(postalCode.value)) {
        searchByPostalCode()
      } else {
        const currentRequest = ++requestId
        getShippingAgencies().then((data) => {
          if (currentRequest === requestId && modelValue.value) provinces.value = data.provinces || []
        }).catch(() => {})
      }
    } else {
      requestId += 1
      loading.value = false
    }
  }
)
</script>

<style scoped>
.blm-overlay {
  position: fixed;
  inset: 0;
  z-index: 1100;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}
.blm-modal {
  width: min(560px, 100%);
  max-height: 85vh;
  background: var(--grey-color, #2a2a2a);
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  font-family: 'Poppins', sans-serif;
}
.blm-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}
.blm-search-context {
  margin-bottom: 0.75rem;
  padding: 0.55rem 0.7rem;
  border-left: 3px solid var(--primary-color, #ffec00);
  background: rgba(255, 236, 0, 0.08);
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.76rem;
  line-height: 1.4;
}
.blm-search-context strong {
  color: #fff;
}
.blm-search-options {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
}
@media (max-width: 480px) {
  .blm-search-options { grid-template-columns: 1fr; }
}
.blm-search-options .form-label { margin-bottom: 0.25rem; }
.blm-nearby {
  margin-left: 0.5rem;
  color: #ffc107;
  font-size: 0.7rem;
}
.blm-title {
  font-size: 1.1rem;
  margin: 0;
}
.blm-body {
  flex: 1;
  overflow-y: auto;
  min-height: 200px;
}
.blm-list {
  display: flex;
  flex-direction: column;
}
.blm-agency {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  width: 100%;
  text-align: left;
  padding: 0.85rem 1.25rem;
  border: none;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  background: transparent;
  color: #fff;
  cursor: pointer;
  transition: background 0.15s;
}
.blm-agency:hover {
  background: rgba(255, 236, 0, 0.08);
}
.blm-agency-info {
  flex: 1;
  min-width: 0;
}
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.2s ease;
}
.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}
</style>
